/**
 * "Your name" (Settings, Security and privacy): the person's own display name, the name reports and comments carry and
 * Foundry Scope signs their reports with. Shows the name the platform holds (from /v1/platform/auth/me), saves a new one
 * with PATCH /v1/platform/me and reads /auth/me again so the app shows it at once. The rules and the words live in
 * lib/settings/display-name; the view is pure, for its tests.
 */
import { useState, type FormEvent } from "react";
import { UserRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { orgsApi } from "@/lib/api/orgs";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { useToast } from "@/lib/ui/toast";
import {
  displayNameProblem,
  displayNameSaveError,
  isPersonName,
  normalizeDisplayName,
} from "@/lib/settings/display-name";

export interface YourNameCardViewProps {
  /** The name the platform holds (users.display_name); undefined while it is being read. Its email until set. */
  current: string | undefined;
  /** What the field shows. */
  value: string;
  saving: boolean;
  /** What is wrong, in plain words, or null. */
  problem: string | null;
  onChange: (value: string) => void;
  onSave: () => void;
}

/** The card itself: the field, a line when no name is set yet, what went wrong, and Save (off until the name changes). */
export function YourNameCardView({ current, value, saving, problem, onChange, onSave }: YourNameCardViewProps) {
  const saved = isPersonName(current) ? current : "";
  const unchanged = normalizeDisplayName(value) === saved;
  const notSetYet = current !== undefined && !isPersonName(current);
  const describedBy = [notSetYet ? "display-name-hint" : "", problem ? "display-name-problem" : ""]
    .filter(Boolean)
    .join(" ");

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSave();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <UserRound className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>Your name</CardTitle>
          <CardDescription>Your name, as it appears on reports and comments.</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} noValidate className="flex max-w-sm flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="display-name">Name</Label>
            <Input
              id="display-name"
              name="display-name"
              autoComplete="name"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={saving}
              aria-invalid={problem ? true : undefined}
              aria-describedby={describedBy || undefined}
            />
            {notSetYet && (
              <span id="display-name-hint" className="text-[11px] text-on-surface-muted">
                No name set yet. Until you set it, some places show your email instead.
              </span>
            )}
            {problem && (
              <p id="display-name-problem" role="alert" className="text-xs text-danger">
                {problem}
              </p>
            )}
          </div>
          <div>
            <Button type="submit" disabled={saving || unchanged}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** The card, wired: the name from the app's /auth/me data, Save through PATCH /v1/platform/me, then /auth/me again. */
export function YourNameCard() {
  const { user, refreshIdentity } = useAuth();
  const toast = useToast();
  const current = user?.displayName;
  // null until the person types: the field then follows the name the platform holds, also once it has been read.
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const value = draft ?? (isPersonName(current) ? current : "");

  const save = async () => {
    if (saving) return;
    const wrong = displayNameProblem(value);
    if (wrong) {
      setProblem(wrong);
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const saved = await orgsApi.updateMyDisplayName(normalizeDisplayName(value));
      setDraft(saved.display_name);
      await refreshIdentity();
      toast.push("ok", "Your name is saved.");
    } catch (err) {
      setProblem(displayNameSaveError(err, value));
    } finally {
      setSaving(false);
    }
  };

  return (
    <YourNameCardView
      current={current}
      value={value}
      saving={saving}
      problem={problem}
      onChange={(v) => {
        setDraft(v);
        setProblem(null);
      }}
      onSave={() => void save()}
    />
  );
}
